/**
 * Shared copy and predicate for a locked screen (DNKADM-0433): a `403` is an
 * authorization answer, never a network failure, so it renders "why" (and who to
 * ask) instead of the "couldn't reach" copy with a Try again that can never work.
 */
import { ApiError } from '@/api/errors';

export const ACCESS_DENIED_TITLE = 'You don’t have access to this';

/** The "why and who to ask" line when neither the screen nor the server names it (DNKADM-0440). */
export const ACCESS_DENIED_FALLBACK = 'Your role doesn’t include this. Ask an owner or admin for access.';

/** The one line for a refused write (`403`) in a money dialog (DNKADM-0480, F-255). */
export const ACCESS_DENIED_ACTION_MESSAGE = 'You don’t have access to do that.';

/** The one line for a `401` the client could not silently refresh (DNKADM-0440). */
export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again to continue.';

/**
 * A screen's own "why and who to ask" for a `403` when it knows which roles read it,
 * e.g. `roleLimitedMessage('The activity log', 'OWNER or ADMIN')`.
 */
export function roleLimitedMessage(subject: string, roles: string): string {
  return (
    `${subject} is limited to staff with the ${roles} role for this tenant. ` +
    'Ask an owner to grant you access, then reload.'
  );
}

/** True for a dink-api `403` problem response. */
export function isForbiddenError(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'FORBIDDEN' || error.status === 403);
}

/** "Alice", "Alice and Bob", "Alice, Bob, and Cy". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * The owner-only invitation notice. Owner names come from the roster already loaded;
 * with none to name it falls back to "your account owner".
 */
export function ownerOnlyInviteMessage(ownerNames: readonly string[]): string {
  const who = ownerNames.length > 0 ? joinNames(ownerNames) : 'your account owner';
  return `Only an owner or Journix support can see and send invitations. Ask ${who} to invite someone.`;
}
